import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { useGetList, useNotify } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { formatBalance } from '../users/formatBalance';
import { TransitionButton } from '../actions/TransitionButton';
import type { CannedResponse } from '../dataProvider';
import {
  MACRO_ACTION_LABEL,
  renderTemplate,
  type TemplateContext,
} from '../cannedResponses/cannedResponsesState';
import type { TicketRow } from './ticketInboxModel';

/**
 * Use a canned response from a ticket context (D-canned-responses, the Phase 6 surface-2
 * library). COPY-OUT ONLY: the agent browses the ACTIVE library, renders a chosen template
 * with the current member's values substituted, and copies the text to wherever support
 * actually replies. This NEVER writes into the ticket (there is no reply/thread model; that
 * is a deferred surface).
 *
 * If the chosen template is a MACRO, the bundled ticket action (resolve or escalate) is
 * offered as a TransitionButton that routes through the SAME capability-guarded chokepoint
 * the inbox buttons use. The run affordance is HIDDEN when the agent lacks the action's
 * capability (cosmetic: the chokepoint is the real boundary, so a macro can never do more
 * than the agent already holds). The credit and suspend actions are never a macro side
 * effect; they stay their own deliberate affordances.
 *
 * The picker itself is gated `canned_responses:read` at the call site (TicketRow).
 */

const ALL_ACTIVE = {
  pagination: { page: 1, perPage: 1000 },
  sort: { field: 'name', order: 'ASC' as const },
  filter: { active: true },
};

export const TemplatePicker = ({ row }: { row: TicketRow }) => {
  const notify = useNotify();
  const { accessibleRegions } = useRegion();
  const { role } = useAccessContext();
  const { data: templates, isLoading } = useGetList<CannedResponse>(
    'cannedResponses',
    ALL_ACTIVE,
  );

  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | ''>('');

  // The bounded substitution context, built from the row's joined member. `balance` is the
  // MEMBER-FACING floored figure (a member reads this copy), never the signed balance.
  const ctx: TemplateContext = useMemo(
    () => ({
      member_name: row.member.name,
      member_email: row.member.email,
      balance: formatBalance(Math.max(0, row.member.balanceSigned), row.market),
      market: row.market.toUpperCase(),
      ticket_subject: row.subject,
      trigger: row.triggeringEntity
        ? `${row.triggeringEntity.kind} #${row.triggeringEntity.id}`
        : null,
    }),
    [row],
  );

  const selected = (templates ?? []).find((t) => t.id === selectedId) ?? null;
  const rendered = selected ? renderTemplate(selected.body, ctx) : '';

  const close = () => {
    setOpen(false);
    setSelectedId('');
  };

  const copyOut = () => {
    // Copy-out only: write to the clipboard, never to the ticket. The clipboard may be
    // unavailable in some environments, so the call is defensive.
    void navigator.clipboard?.writeText(rendered);
    notify('Template copied', { type: 'success' });
  };

  const macroPermitted =
    selected?.kind === 'macro' &&
    selected.action != null &&
    hasAccess(
      { role, accessibleRegions },
      { capability: selected.action.capability },
    );

  return (
    <>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        Use a template
      </Button>
      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>Use a template for {row.member.name}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Pick a template, check the rendered text, and copy it out. Using a
            template never changes the ticket. A macro also offers its bundled
            action.
          </DialogContentText>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id={`template-${row.id}`}>Template</InputLabel>
            <Select
              labelId={`template-${row.id}`}
              label="Template"
              value={selectedId === '' ? '' : String(selectedId)}
              inputProps={{ 'data-testid': 'template-select' }}
              onChange={(e) => setSelectedId(Number(e.target.value))}
            >
              {(templates ?? []).map((t) => (
                <MenuItem key={t.id} value={String(t.id)}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isLoading && !templates ? null : selected ? (
            <Box
              data-testid="template-rendered"
              sx={{
                p: 2,
                mb: 1,
                whiteSpace: 'pre-wrap',
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              {rendered}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Close</Button>
          <Stack direction="row" spacing={1}>
            {macroPermitted && selected?.action && (
              <TransitionButton
                resource="tickets"
                capability={selected.action.capability}
                id={row.id}
                toStatus={selected.action.toStatus}
                previousData={{ id: row.id, status: row.status }}
                label={`Run: ${MACRO_ACTION_LABEL[selected.action.toStatus]}`}
                color="primary"
                confirmTitle="Run the macro action"
                confirmContent="This runs the template's bundled ticket action."
                successMessage="Macro action done"
              />
            )}
            <Button variant="contained" disabled={!selected} onClick={copyOut}>
              Copy
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
};
